import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Star, Clock, Edit, Trash2, CheckCircle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ReviewModal from '../modals/ReviewModal';

interface Review {
  id: string;
  expert_id: string;
  rating: number;
  comment: string;
  date: string;
  verified: boolean;
  expert_name?: string;
  user_name?: string;
}

interface UserProfile {
  id: string;
  name: string;
  [key: string]: any;
}

interface ReviewsSectionProps {
  user: UserProfile;
}

// ── Mock data for complete UI/UX preview ──

const mockGivenReviews: Review[] = [
  { id: 'g1', expert_id: 'e1', rating: 5, comment: 'Wonderful session! She really helped me with my anxiety and gave practical tools to manage stress.', date: '2024-01-08', verified: true, expert_name: 'Dr. Emily Davis' },
  { id: 'g2', expert_id: 'e2', rating: 4, comment: 'Very professional and insightful. Highly recommend for career counseling.', date: '2023-12-20', verified: true, expert_name: 'Dr. Robert Wilson' },
  { id: 'g3', expert_id: 'e3', rating: 5, comment: 'Life-changing guidance. I feel more confident about my future now.', date: '2023-11-15', verified: false, expert_name: 'Dr. Ananya Mehta' },
];

const mockPendingSessions = [
  { id: 'ps1', expert_id: 'e4', appointment_date: '2024-01-18', expert: { name: 'Dr. Sarah Johnson', profile_picture: '', id: 'e4' } },
  { id: 'ps2', expert_id: 'e5', appointment_date: '2024-01-15', expert: { name: 'Dr. Michael Chen', profile_picture: '', id: 'e5' } },
];

const mockReceivedReviews = [
  { id: 'rr1', expert_name: 'Dr. Emily Davis', rating: 5, comment: 'Very engaged and committed to the process. A pleasure to work with. Shows remarkable progress between sessions.', date: '2024-01-09' },
  { id: 'rr2', expert_name: 'Dr. Robert Wilson', rating: 5, comment: 'Great client, very open and receptive to feedback. Always comes prepared for sessions.', date: '2023-12-21' },
  { id: 'rr3', expert_name: 'Dr. Ananya Mehta', rating: 4, comment: 'Dedicated and punctual. Implements suggestions effectively and shows consistent improvement.', date: '2023-11-18' },
];

const ReviewsSection: React.FC<ReviewsSectionProps> = ({ user }) => {
  const [reviews, setReviews] = useState<Review[]>(mockGivenReviews);
  const [loading, setLoading] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [completedSessions, setCompletedSessions] = useState<any[]>(mockPendingSessions);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [receivedReviews, setReceivedReviews] = useState<any[]>(mockReceivedReviews);

  const handleAddReview = (session: any) => {
    setSelectedReview(null);
    setSelectedSession(session);
    setIsReviewModalOpen(true);
  };

  const handleEditReview = (review: Review) => {
    setSelectedReview(review);
    setSelectedSession(null);
    setIsReviewModalOpen(true);
  };

  const handleDeleteReview = (reviewId: string) => {
    if (!confirm('Are you sure you want to delete this review?')) return;
    setReviews(prev => prev.filter(r => r.id !== reviewId));
    toast.success('Review deleted successfully');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/30'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            Reviews & Ratings
          </CardTitle>
        </CardHeader>
      </Card>

      <Tabs defaultValue="given" className="w-full">
        <TabsList className="w-full max-w-md">
          <TabsTrigger value="given" className="flex-1">My Reviews</TabsTrigger>
          <TabsTrigger value="received" className="flex-1">Reviews About Me</TabsTrigger>
        </TabsList>

        {/* Reviews Given Tab */}
        <TabsContent value="given" className="space-y-6">
          {/* Sessions awaiting review */}
          {completedSessions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-yellow-500" />
                  Pending Reviews ({completedSessions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {completedSessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-4 border rounded-lg bg-yellow-50/50 border-yellow-200/50"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={session.expert?.profile_picture} />
                          <AvatarFallback>
                            {session.expert?.name?.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{session.expert?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Session on {formatDate(session.appointment_date)}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAddReview(session)}
                      >
                        <Star className="h-4 w-4 mr-1" />
                        Write Review
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Existing reviews */}
          {reviews.length === 0 && completedSessions.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <Star className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">You haven't written any reviews yet</p>
                <p className="text-sm text-muted-foreground">
                  Complete sessions with experts to leave reviews and ratings
                </p>
              </CardContent>
            </Card>
          ) : reviews.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Reviews Written ({reviews.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-semibold">{review.expert_name || 'Expert'}</h3>
                          {review.verified && (
                            <Badge variant="secondary" className="bg-green-100 text-green-800">
                              Verified
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex">{renderStars(review.rating)}</div>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(review.date)}
                          </span>
                        </div>
                        
                        {review.comment && (
                          <p className="text-muted-foreground">{review.comment}</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditReview(review)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteReview(review.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reviews Received Tab */}
        <TabsContent value="received" className="space-y-6">
          {receivedReviews.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground mb-2">No reviews received yet</p>
                <p className="text-sm text-muted-foreground">
                  Experts you've worked with can leave reviews about your sessions
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {receivedReviews.map((review: any) => (
                <Card key={review.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Avatar>
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {review.expert_name?.slice(0, 2).toUpperCase() || 'E'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold">{review.expert_name || 'Expert'}</h3>
                          <span className="text-sm text-muted-foreground">{formatDate(review.date)}</span>
                        </div>
                        <div className="flex mb-2">{renderStars(review.rating)}</div>
                        {review.comment && (
                          <p className="text-muted-foreground">{review.comment}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        review={selectedReview}
        session={selectedSession}
        userId={user.id}
        userName={user.name}
        onReviewSubmitted={() => {
          // Mock: refresh would happen here
        }}
      />
    </div>
  );
};

export default ReviewsSection;
